/**
 * M27 — write-only secret input + presence indicator (ADM §2.1). Shared by
 * every integration form (models, identity providers, connector OAuth, web
 * research). The stored secret NEVER reaches the browser: the response carries
 * only a `hasSecret` presence boolean, so this field renders an always-empty
 * password input plus a "key set ✓" / "no key set" indicator — the value is
 * never rendered. On edit, leaving the field blank keeps the stored secret.
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface SecretFieldProps {
  id: string;
  label: string;
  /** Whether a secret is already stored (drives the presence indicator). */
  hasSecret: boolean;
  /** The new secret being typed (write-only; empty keeps the stored one). */
  value: string;
  onChange: (value: string) => void;
  /** When true the field is required (create forms with no stored secret). */
  required?: boolean;
  placeholder?: string;
}

export function SecretField({
  id,
  label,
  hasSecret,
  value,
  onChange,
  required,
  placeholder,
}: SecretFieldProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{label}</Label>
        <span
          className={hasSecret ? 'text-xs font-medium text-emerald-600' : 'text-xs text-muted-foreground'}
          data-has-secret={hasSecret ? 'true' : 'false'}
        >
          {hasSecret ? 'Key set ✓' : 'No key set'}
        </span>
      </div>
      <Input
        id={id}
        type="password"
        autoComplete="off"
        placeholder={placeholder ?? (hasSecret ? 'Leave blank to keep the stored key' : undefined)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required && !hasSecret}
      />
      <p className="text-xs text-muted-foreground">
        {hasSecret
          ? 'Stored encrypted; never shown again. Enter a new value to replace it.'
          : 'Stored encrypted; never shown again.'}
      </p>
    </div>
  );
}
